'use client'

import { useState } from 'react'
import { toast } from 'sonner'
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { popModal } from '@/components/modals'
import { useTranslations } from 'next-intl'

interface RenameWorkspaceModalProps {
  workspaceId: string
  currentName: string
  onSuccess?: () => void
}

/**
 * Modal for renaming a workspace
 * @param workspaceId - The ID of the workspace to rename
 * @param currentName - The current name of the workspace
 * @param onSuccess - Optional callback called after successful rename
 */
export default function RenameWorkspaceModal({
  workspaceId,
  currentName,
  onSuccess,
}: RenameWorkspaceModalProps) {
  const t = useTranslations('rename-workspace')
  const [name, setName] = useState(currentName)
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const trimmedName = name.trim()

    if (!trimmedName) {
      toast.error(t('workspace_name_cannot_be_empty'))
      return
    }

    if (trimmedName.length > 100) {
      toast.error(t('workspace_name_must_be_100_characters_or_less'))
      return
    }

    if (trimmedName === currentName) {
      popModal()
      return
    }

    try {
      setLoading(true)

      const response = await fetch(`/api/me/workspaces/${workspaceId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: trimmedName,
        }),
      })

      const result = await response.json()

      if (!response.ok || !result.success) {
        throw new Error(result.message || t('rename_workspace_error'))
      }

      toast.success(t('rename_workspace_success'))
      onSuccess?.()
      popModal()
    } catch (error) {
      console.error('Error renaming workspace:', error)
      toast.error(
        error instanceof Error ? error.message : t('rename_workspace_error'),
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>{t('rename_workspace')}</DialogTitle>
        <DialogDescription>
          {t('rename_workspace_description')}
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="workspace-name">{t('workspace_name')}</Label>
          <Input
            id="workspace-name"
            type="text"
            value={name}
            onChange={(e) => {
              return setName(e.target.value)
            }}
            placeholder={t('enter_workspace_name')}
            maxLength={100}
            autoFocus
            disabled={loading}
            required
          />
          <p className="text-xs text-muted-foreground">
            {t('workspace_name_length', { length: name.length, max: 100 })}
          </p>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline_2"
            onClick={() => {
              return popModal()
            }}
            disabled={loading}
          >
            {t('cancel')}
          </Button>
          <Button
            variant={'active'}
            type="submit"
            disabled={loading || !name.trim()}
          >
            {loading ? t('renaming') : t('rename')}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  )
}
